import React, { useState, useEffect } from 'react';
import { Save, Check, AlertCircle, Info } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../utils/api';

const FA_LABELS = ['FA1', 'FA2', 'FA3', 'FA4'];

// Default Telangana SCERT FA chapter plan (admin can override)
const DEFAULT_SCHEDULE = {
  fa1: '1-3',
  fa2: '4-6',
  fa3: '7-9',
  fa4: '10-12',
};

function parseChapterRange(str) {
  if (!str) return [];
  const nums = [];
  str.split(',').forEach(part => {
    part = part.trim();
    if (part.includes('-')) {
      const [start, end] = part.split('-').map(Number);
      for (let i = start; i <= end && i - start < 20; i++) nums.push(i);
    } else if (!isNaN(Number(part))) {
      nums.push(Number(part));
    }
  });
  return [...new Set(nums)].sort((a, b) => a - b);
}

function FAScheduleConfig() {
  const { classes, subjects } = useApp();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  const [schedule, setSchedule] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Selected class/subject to edit
  const [selClass, setSelClass] = useState('');
  const [selSubject, setSelSubject] = useState('');

  useEffect(() => { loadSchedule(); }, []);

  async function loadSchedule() {
    try {
      const data = await api.getFASchedule();
      setSchedule(Array.isArray(data) ? data : []);
    } catch {
      setSchedule([]);
    }
  }

  function getEntry(classId, subjectId) {
    return schedule.find(e => e.classId === classId && e.subjectId === subjectId) || {
      classId, subjectId,
      fa1: DEFAULT_SCHEDULE.fa1,
      fa2: DEFAULT_SCHEDULE.fa2,
      fa3: DEFAULT_SCHEDULE.fa3,
      fa4: DEFAULT_SCHEDULE.fa4,
    };
  }

  function updateEntry(classId, subjectId, fa, value) {
    setSchedule(prev => {
      const exists = prev.find(e => e.classId === classId && e.subjectId === subjectId);
      if (exists) {
        return prev.map(e => e.classId === classId && e.subjectId === subjectId
          ? { ...e, [fa]: value }
          : e
        );
      }
      const defaults = getEntry(classId, subjectId);
      return [...prev, { ...defaults, [fa]: value }];
    });
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await api.saveFASchedule(schedule);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  }

  if (!isAdmin) {
    return (
      <div className="bg-white rounded-xl p-8 text-center text-gray-400">
        <Info size={40} className="mx-auto mb-3 opacity-30" />
        <p>Only admins can configure FA schedules.</p>
      </div>
    );
  }

  const entry = selClass && selSubject ? getEntry(selClass, selSubject) : null;
  const cls = classes.find(c => c.id === selClass);
  const sub = subjects.find(s => s.id === selSubject);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">FA Schedule Configuration</h1>
          <p className="text-gray-500 text-sm">Define which chapters each Formative Assessment covers, per class and subject</p>
        </div>
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-60">
          {saved ? <><Check size={14} /> Saved!</> : <><Save size={14} /> {saving ? 'Saving…' : 'Save All'}</>}
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 rounded-lg px-4 py-2 text-sm">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-5 text-sm text-blue-700 flex items-start gap-2">
        <Info size={15} className="mt-0.5 flex-shrink-0" />
        <div>
          Enter chapter numbers as ranges like <strong>1-3</strong> or comma-separated like <strong>1,2,3</strong> or mixed like <strong>1-3,5</strong>.
          The default Telangana SCERT plan is pre-filled (FA1: Ch 1–3, FA2: Ch 4–6, FA3: Ch 7–9, FA4: Ch 10–12).
          Change only the entries that differ for a specific class/subject.
        </div>
      </div>

      {/* Class + Subject selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-5">
        <p className="text-sm font-medium text-gray-700 mb-3">Select class and subject to configure:</p>
        <div className="flex gap-3 flex-wrap">
          <select value={selClass} onChange={e => { setSelClass(e.target.value); setSelSubject(''); }}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
            <option value="">-- Select Class --</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
          </select>
          {selClass && (
            <select value={selSubject} onChange={e => setSelSubject(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
              <option value="">-- Select Subject --</option>
              {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* FA Chapter Entry */}
      {entry && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-5">
          <h2 className="font-semibold text-gray-800 mb-1">
            {cls?.name}-{cls?.section} · {sub?.name}
          </h2>
          <p className="text-xs text-gray-400 mb-4">Configure chapter coverage for each Formative Assessment</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {FA_LABELS.map(fa => {
              const faKey = fa.toLowerCase();
              const val = entry[faKey] || '';
              const parsed = parseChapterRange(val);
              return (
                <div key={fa} className="border border-gray-200 rounded-lg p-3">
                  <label className="block text-sm font-semibold text-indigo-700 mb-1">{fa}</label>
                  <input
                    value={val}
                    onChange={e => updateEntry(selClass, selSubject, faKey, e.target.value)}
                    placeholder="e.g. 1-3"
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  />
                  {parsed.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Chapters: {parsed.join(', ')}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Summary table of all configured entries */}
      {schedule.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h2 className="font-semibold text-gray-800">Configured Schedules</h2>
            <p className="text-xs text-gray-400">{schedule.length} class-subject combination{schedule.length !== 1 ? 's' : ''} configured</p>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-2 text-left font-semibold">Class</th>
                <th className="px-4 py-2 text-left font-semibold">Subject</th>
                <th className="px-4 py-2 text-left font-semibold">FA1</th>
                <th className="px-4 py-2 text-left font-semibold">FA2</th>
                <th className="px-4 py-2 text-left font-semibold">FA3</th>
                <th className="px-4 py-2 text-left font-semibold">FA4</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((e, i) => {
                const c = classes.find(c => c.id === e.classId);
                const s = subjects.find(s => s.id === e.subjectId);
                return (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-2">{c ? `${c.name}-${c.section}` : e.classId}</td>
                    <td className="px-4 py-2">{s?.name || e.subjectId}</td>
                    <td className="px-4 py-2 text-gray-500">{e.fa1 || '—'}</td>
                    <td className="px-4 py-2 text-gray-500">{e.fa2 || '—'}</td>
                    <td className="px-4 py-2 text-gray-500">{e.fa3 || '—'}</td>
                    <td className="px-4 py-2 text-gray-500">{e.fa4 || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default FAScheduleConfig;
